import React, { createContext, ReactNode, useEffect, useRef, useState } from "react"
import { jwtDecode } from "jwt-decode"
import type { KeycloakTokenParsed } from "keycloak-js"

import { colors } from "@/components/colors"
import keycloakInstance from "./keycloak"
import { exchangeOtt } from "@/app/api/auth"

import type { AuthContextProps } from "./types.ts"
import { setInterCeptor } from "@/app/api"

// StrictMode 중복 실행 방지용 module-level 플래그
let ottExchangeAttempted = false

const AuthContext = createContext<AuthContextProps>({
  token: undefined,
  authenticated: false,
  login: undefined,
  logout: undefined,
  updateToken: undefined,
})

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false)
  const [token, setToken] = useState<string | undefined>()
  const [isInitialized, setIsInitialized] = useState(false)
  const refreshIntervalRef = useRef<number | null>(null)

  useEffect(() => {
    setInterCeptor(token)
  }, [token])

  useEffect(() => {
    if (keycloakInstance.didInitialize) {
      return
    }

    // URL에서 OTT 파라미터 확인
    const urlParams = new URLSearchParams(window.location.search)
    const ott = urlParams.get("ott")

    if (ott && !ottExchangeAttempted) {
      // OTT 기반 인증: refreshToken의 azp가 다른 client이므로
      // keycloak init에 토큰을 넘기지 않고 수동 주입한다.
      // refresh 불가 → onTokenExpired, interval 설정 안 함
      ottExchangeAttempted = true

      // URL에서 ott 파라미터 제거
      urlParams.delete("ott")
      const newSearch = urlParams.toString()
      const newUrl =
        window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash
      window.history.replaceState(null, "", newUrl)

      // keycloak을 먼저 init해야 실패해도 login/logout 함수 사용 가능
      keycloakInstance
        .init({ checkLoginIframe: false, enableLogging: true })
        .then(() => exchangeOtt(ott))
        .then(({ accessToken }) => {
          // keycloak 내부 setToken이 하는 것과 동일하게 수동 주입
          const tokenParsed = jwtDecode<KeycloakTokenParsed>(accessToken)
          keycloakInstance.token = accessToken
          keycloakInstance.tokenParsed = tokenParsed
          keycloakInstance.authenticated = true
          keycloakInstance.subject = tokenParsed.sub
          keycloakInstance.realmAccess = tokenParsed.realm_access
          keycloakInstance.resourceAccess = tokenParsed.resource_access
          keycloakInstance.sessionId = tokenParsed.sid

          setToken(accessToken)
          setAuthenticated(true)
          setIsInitialized(true)
        })
        .catch((error) => {
          console.error("OTT authentication failed:", error)
          setIsInitialized(true)
          setAuthenticated(false)
        })
    } else if (!ott) {
      // 기존 Keycloak 로그인 흐름
      keycloakInstance.onTokenExpired = () => {
        keycloakInstance
          .updateToken(180)
          .then((refreshed) => {
            if (refreshed) {
              setToken(keycloakInstance.token)
              setAuthenticated(true)
            }
          })
          .catch(() => {
            keycloakInstance.logout({ redirectUri: window.location.origin }).then(() => {
              setToken(undefined)
              setAuthenticated(false)
            })
          })
      }

      keycloakInstance
        .init({ onLoad: "check-sso", checkLoginIframe: false, enableLogging: true })
        .then((auth) => {
          setToken(keycloakInstance.token)
          setAuthenticated(auth)
          setIsInitialized(true)
          if (auth) {
            if (!refreshIntervalRef.current) {
              refreshIntervalRef.current = window.setInterval(() => {
                keycloakInstance
                  .updateToken(180)
                  .then((refreshed) => {
                    if (refreshed) {
                      setToken(keycloakInstance.token)
                      setAuthenticated(true)
                    }
                  })
                  .catch(() => {
                    console.error("Token Batch Failed.")
                  })
              }, 60 * 1000)
            }
          }
        })
        .catch(() => {
          console.error("Keycloak initialization failed")
        })
    }

    return () => {
      clearInterval(refreshIntervalRef.current ?? undefined)
      refreshIntervalRef.current = null
    }
  }, [])

  const updateToken = async () => {
    await keycloakInstance.updateToken(-1) // 강제 갱신
    setToken(keycloakInstance.token)
  }

  // 초기화만 완료되면 렌더링 (인증 여부는 개별 라우트에서 처리)
  if (!isInitialized) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: colors.neutral.grey["100"],
        }}>
        <div
          style={{
            textAlign: "center",
          }}>
          <img
            src="/images/logo_horizontal.png"
            alt="Fitogether logo"
            width={200}
            height={30}
            style={{ marginBottom: "20px" }}
          />
          {/*<div style={{ fontSize: "16px", color: "#666" }}>인증 중입니다...</div>*/}
          {/*<div style={{ fontSize: "12px", color: "#999", marginTop: "10px" }}>*/}
          {/*  로그인 페이지로 리다이렉트되지 않는 경우, 개발자 도구의 콘솔을 확인하세요.*/}
          {/*</div>*/}
          {/*<button*/}
          {/*  onClick={() => keycloakInstance.login()}*/}
          {/*  style={{*/}
          {/*    marginTop: "20px",*/}
          {/*    padding: "10px 20px",*/}
          {/*    backgroundColor: "#007bff",*/}
          {/*    color: "white",*/}
          {/*    border: "none",*/}
          {/*    borderRadius: "4px",*/}
          {/*    cursor: "pointer",*/}
          {/*  }}>*/}
          {/*  수동으로 로그인*/}
          {/*</button>*/}
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        authenticated,
        login: keycloakInstance.login,
        logout: keycloakInstance.logout,
        updateToken,
      }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => React.useContext(AuthContext)
